import { BASE_API_URL, BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import store from "@/redux/store";
import {
  chatBackendKindForNewConversation,
  type ChatBackendUserData,
} from "@/components/FpPages/Chat/selectChatBackend";
import { toConversationSlug } from "@/components/FpPages/Chat/chatUtils";
import { v4 as uuidv4 } from "uuid";

export interface SelectedFileInfo {
  id: string;
  name: string;
  type: string;
  /** Finpilot's processing of this file failed (#13887): still attached and
   *  sent, and its chip says so. */
  processingFailed?: boolean;
}

// Shape of the localStorage handoff written for the new chat tab. Both chat
// pages parse this in their readFileSelectionStorage — producer and readers
// must agree, which is exactly what broke in #11075's chip half (ids written
// without names were silently dropped by the readers).
export interface ChatFileSelectionHandoff {
  selectedFileIds?: string[];
  selectedFiles?: SelectedFileInfo[];
  folderPaths?: string[][];
}

const chatWithDocuments = async ({
  selectedNodes,
  fileIds,
  folderPaths,
  files,
}: {
  selectedNodes?: { id: string; isFile: boolean }[];
  fileIds?: string[];
  folderPaths?: string[][];
  files?: SelectedFileInfo[];
}) => {
  const nodes =
    selectedNodes ||
    (fileIds ? fileIds.map((id) => ({ id, isFile: true })) : []);

  // Engine routing (#11075): create the chat in whichever store the org's
  // chat engine reads, or the new tab 404s on the id and the #9955 fallback
  // probe silently drops the conversation to the other engine.
  // The store types userData's initial state with null-heavy placeholder
  // fields; narrow to the decision function's structural view of it.
  const backendKind = chatBackendKindForNewConversation(
    store.getState().userData as ChatBackendUserData
  );

  // The id the chat page reads the localStorage handoff under, and the id
  // segment of the customer-facing URL. For agentic conversations these
  // differ: the page looks the key up by the internal pgid id, while the URL
  // always shows the bare uuid.
  let storageId: string;
  let urlSlug: string;

  if (backendKind === "agentic") {
    // Mirrors AgenticChatBackend.createWorkspace() — the class itself serves
    // the conversation page and isn't imported from this entry-point module.
    const response = await fpFetch(
      `${BASE_API_URL_V2}/api/v2/chat/conversations`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attached_blob_ids: [] }),
      }
    );
    if (!response.ok) {
      return;
    }
    const conv = (await response.json()) as { conversation_id: string };
    storageId = conv.conversation_id;
    urlSlug = toConversationSlug(conv.conversation_id);
  } else {
    const newWorkspace = {
      _id: uuidv4(),
      type: "chat",
      meta: {
        src: "chat_with_documents",
      },
    };
    const response = await fpFetch(`${BASE_API_URL}/workspaces/update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(newWorkspace),
    });
    if (!response.ok) {
      return;
    }
    storageId = newWorkspace._id;
    urlSlug = newWorkspace._id;
  }

  const nodeIds = nodes.map((node) => node.id);

  // Store file selections in localStorage to avoid URL length limits.
  // localStorage is shared across same-origin tabs, so the new tab can read it.
  const selectionData: ChatFileSelectionHandoff = {};
  if (nodeIds.length > 0) {
    selectionData.selectedFileIds = nodeIds;
  }
  if (files && files.length > 0) {
    selectionData.selectedFiles = files;
  }
  if (folderPaths && folderPaths.length > 0) {
    selectionData.folderPaths = folderPaths;
  }
  if (Object.keys(selectionData).length > 0) {
    localStorage.setItem(
      `chat_with_documents_${storageId}`,
      JSON.stringify(selectionData)
    );
  }

  window.open(
    `/chat/${urlSlug}?src=chat_with_documents`,
    "_blank",
    "noopener,noreferrer"
  );
};

export default chatWithDocuments;
